/**
 * Form Data Hook
 *
 * Loads form data from Supabase and converts it to the format
 * expected by the new Form Builder and Form Player components.
 * Handles both v1 (legacy) and v2 (new) form schemas.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  QuestionType,
  QuestionConfig,
  mapLegacyQuestionType,
} from '@/lib/forms/question-types';
import {
  FormConfig,
  FormQuestion,
} from '@/components/form-builder/hooks/useFormBuilder';
import { NavigableQuestion } from '@/components/form-player/hooks/useFormNavigation';

// Database types
interface DBQuestion {
  id: string;
  form_id: string;
  type: string;
  text: string;
  required: boolean;
  order: number;
  options: { id: string; value: string }[] | null;
  placeholder: string | null;
  visible_on_high_rating: boolean;
  visible_on_low_rating: boolean;
  branching_rules: unknown | null;
  question_type_v2: string | null;
  validation_rules: unknown | null;
  device_config: unknown | null;
  ui_config: unknown | null;
  animation_config: unknown | null;
}

interface DBReviewForm {
  id: string;
  business_id: string;
  title: string;
  active_status: boolean;
  enable_step_2: boolean;
  enable_step_2_high: boolean;
  enable_step_2_low: boolean;
  builder_version: number | null;
  mobile_config: unknown | null;
  desktop_config: unknown | null;
  animation_settings: unknown | null;
  keyboard_nav_enabled: boolean | null;
  progress_bar_config: unknown | null;
  ui_text_config: unknown | null;
  high_rating_email_enabled: boolean;
  low_rating_email_enabled: boolean;
  reward_enabled: boolean;
  ai_mode_enabled: boolean;
}

interface DBBusiness {
  id: string;
  name: string;
  review_redirect_star_threshold: number;
  google_place_id: string | null;
}

// Hook options
export interface UseFormDataOptions {
  formId: string;
}

// Return type
export interface FormData {
  form: FormConfig;
  questions: FormQuestion[];
  business: DBBusiness;
  isV2: boolean;
  googleReviewUrl: string | null;
}

export function useFormData(options: UseFormDataOptions) {
  const { formId } = options;

  const [data, setData] = useState<FormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!formId) {
      setError(new Error('Form ID is required'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch form with questions and business
      const { data: formData, error: formError } = await supabase
        .from('review_forms')
        .select(`
          *,
          businesses!inner (
            id,
            name,
            review_redirect_star_threshold,
            google_place_id
          ),
          questions (*)
        `)
        .eq('id', formId)
        .single();

      if (formError) throw formError;
      if (!formData) throw new Error('Form not found');

      const dbForm = formData as DBReviewForm & {
        businesses: DBBusiness;
        questions: DBQuestion[];
      };

      const isV2 = (dbForm.builder_version ?? 1) >= 2;

      // Convert to FormConfig
      const form: FormConfig = {
        id: dbForm.id,
        title: dbForm.title,
        businessId: dbForm.business_id,
        builderVersion: dbForm.builder_version ?? 1,
        activeStatus: dbForm.active_status,
        enableStep2: dbForm.enable_step_2 ?? true,
        enableStep2High: dbForm.enable_step_2_high ?? true,
        enableStep2Low: dbForm.enable_step_2_low ?? true,
        mobileConfig: (dbForm.mobile_config as FormConfig['mobileConfig']) ?? {
          questionOrder: [],
          enabledQuestions: [],
        },
        desktopConfig: (dbForm.desktop_config as FormConfig['desktopConfig']) ?? {
          questionOrder: [],
          enabledQuestions: [],
        },
        animationSettings: (dbForm.animation_settings as FormConfig['animationSettings']) ?? {
          enabled: true,
          type: 'slide',
          springStiffness: 300,
          springDamping: 30,
        },
        keyboardNavEnabled: dbForm.keyboard_nav_enabled ?? true,
        progressBarConfig: (dbForm.progress_bar_config as FormConfig['progressBarConfig']) ?? {
          enabled: true,
          style: 'bar',
          showPercentage: false,
        },
        uiTextConfig: dbForm.ui_text_config as FormConfig['uiTextConfig'],
      };

      // Convert questions
      const questions: FormQuestion[] = (dbForm.questions || [])
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((q) => convertDBQuestion(q, isV2));

      // Build question order if not present in v2 config
      if (isV2 && form.mobileConfig.questionOrder.length === 0) {
        const questionIds = questions.map((q) => q.id);
        form.mobileConfig.questionOrder = questionIds;
        form.mobileConfig.enabledQuestions = questionIds;
        form.desktopConfig.questionOrder = questionIds;
        form.desktopConfig.enabledQuestions = questionIds;
      }

      // Build Google review URL
      const googleReviewUrl = dbForm.businesses.google_place_id
        ? `https://search.google.com/local/writereview?placeid=${dbForm.businesses.google_place_id}`
        : null;

      setData({
        form,
        questions,
        business: dbForm.businesses,
        isV2,
        googleReviewUrl,
      });
    } catch (err) {
      console.error('Error fetching form data:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [formId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  };
}

// Helper: Convert DB question to FormQuestion
function convertDBQuestion(dbQuestion: DBQuestion, isV2: boolean): FormQuestion {
  // Determine question type
  let questionType: QuestionType;
  if (isV2 && dbQuestion.question_type_v2) {
    questionType = dbQuestion.question_type_v2 as QuestionType;
  } else {
    questionType = mapLegacyQuestionType(dbQuestion.type);
  }

  // Convert options
  const options = (dbQuestion.options || []).map((opt, idx) => ({
    id: opt.id || String(idx),
    label: opt.value,
    value: opt.value.toLowerCase().replace(/\s+/g, '_'),
  }));

  // Build validation rules
  const validationRules = dbQuestion.validation_rules as FormQuestion['validationRules'] || {};

  // Build UI config
  const uiConfig = (dbQuestion.ui_config as FormQuestion['uiConfig']) || {
    placeholder: dbQuestion.placeholder || undefined,
    showLetterIndicators: true,
  };

  // Build device config
  const deviceConfig = (dbQuestion.device_config as FormQuestion['deviceConfig']) || {
    mobile: { order: dbQuestion.order ?? 0, visible: true },
    desktop: { order: dbQuestion.order ?? 0, visible: true },
  };

  // Convert branching rules
  const branchingRules = convertBranchingRules(dbQuestion.branching_rules);

  return {
    id: dbQuestion.id,
    formId: dbQuestion.form_id,
    type: questionType,
    text: dbQuestion.text,
    required: dbQuestion.required ?? false,
    placeholder: dbQuestion.placeholder || undefined,
    options: options.length > 0 ? options : undefined,
    validationRules,
    uiConfig,
    deviceConfig,
    branchingRules,
    visibleOnHighRating: dbQuestion.visible_on_high_rating ?? true,
    visibleOnLowRating: dbQuestion.visible_on_low_rating ?? true,
    createdAt: new Date().toISOString(),
  };
}

// Helper: Convert legacy branching rules to v2 format
function convertBranchingRules(dbRules: unknown): FormQuestion['branchingRules'] {
  if (!dbRules) return undefined;

  const rules = dbRules as {
    answers?: {
      [key: string]: {
        action: string;
        targetQuestionId?: string;
      };
    };
  };

  if (!rules.answers) return undefined;

  return Object.entries(rules.answers).map(([answerValue, action]) => ({
    conditionType: 'equals' as const,
    conditionValue: answerValue,
    targetQuestionId:
      action.action === 'skip_to_end'
        ? 'end'
        : action.action === 'goto_question'
        ? action.targetQuestionId || 'end'
        : 'end',
  }));
}

// Hook for saving form data
export function useSaveFormData() {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const saveForm = useCallback(
    async (form: FormConfig, questions: FormQuestion[], isNewForm: boolean = false) => {
      try {
        setSaving(true);
        setError(null);

        let actualFormId = form.id;

        if (isNewForm) {
          // INSERT new form into database
          const { data: newForm, error: insertFormError } = await supabase
            .from('review_forms')
            .insert({
              business_id: form.businessId,
              title: form.title,
              active_status: form.activeStatus ?? false,
              enable_step_2: form.enableStep2 ?? true,
              enable_step_2_high: form.enableStep2High ?? true,
              enable_step_2_low: form.enableStep2Low ?? true,
              builder_version: 2,
              mobile_config: form.mobileConfig,
              desktop_config: form.desktopConfig,
              animation_settings: form.animationSettings,
              keyboard_nav_enabled: form.keyboardNavEnabled ?? true,
              progress_bar_config: form.progressBarConfig,
              ui_text_config: form.uiTextConfig,
              // Review settings - email
              high_rating_email_enabled: form.reviewSettings?.highRatingEmailEnabled ?? false,
              high_rating_email_required: form.reviewSettings?.highRatingEmailRequired ?? false,
              low_rating_email_enabled: form.reviewSettings?.lowRatingEmailEnabled ?? false,
              low_rating_email_required: form.reviewSettings?.lowRatingEmailRequired ?? false,
              // Review settings - rewards
              high_rating_reward_type: form.reviewSettings?.highRatingRewardType ?? 'none',
              high_rating_coupon_offer_id: form.reviewSettings?.highRatingCouponOfferId || null,
              high_rating_loyalty_program_id: form.reviewSettings?.highRatingLoyaltyProgramId || null,
              low_rating_reward_type: form.reviewSettings?.lowRatingRewardType ?? 'none',
              low_rating_coupon_offer_id: form.reviewSettings?.lowRatingCouponOfferId || null,
              low_rating_loyalty_program_id: form.reviewSettings?.lowRatingLoyaltyProgramId || null,
            })
            .select()
            .single();

          if (insertFormError) throw insertFormError;
          actualFormId = newForm.id;

          // Update business star threshold if changed
          if (form.reviewSettings?.starThreshold !== undefined) {
            await supabase
              .from('businesses')
              .update({ review_redirect_star_threshold: form.reviewSettings.starThreshold })
              .eq('id', form.businessId);
          }
        } else {
          // UPDATE existing form
          const { error: formError } = await supabase
            .from('review_forms')
            .update({
              title: form.title,
              active_status: form.activeStatus,
              enable_step_2: form.enableStep2,
              enable_step_2_high: form.enableStep2High,
              enable_step_2_low: form.enableStep2Low,
              builder_version: 2,
              mobile_config: form.mobileConfig,
              desktop_config: form.desktopConfig,
              animation_settings: form.animationSettings,
              keyboard_nav_enabled: form.keyboardNavEnabled,
              progress_bar_config: form.progressBarConfig,
              ui_text_config: form.uiTextConfig,
              // Review settings - email
              high_rating_email_enabled: form.reviewSettings?.highRatingEmailEnabled ?? false,
              high_rating_email_required: form.reviewSettings?.highRatingEmailRequired ?? false,
              low_rating_email_enabled: form.reviewSettings?.lowRatingEmailEnabled ?? false,
              low_rating_email_required: form.reviewSettings?.lowRatingEmailRequired ?? false,
              // Review settings - rewards
              high_rating_reward_type: form.reviewSettings?.highRatingRewardType ?? 'none',
              high_rating_coupon_offer_id: form.reviewSettings?.highRatingCouponOfferId || null,
              high_rating_loyalty_program_id: form.reviewSettings?.highRatingLoyaltyProgramId || null,
              low_rating_reward_type: form.reviewSettings?.lowRatingRewardType ?? 'none',
              low_rating_coupon_offer_id: form.reviewSettings?.lowRatingCouponOfferId || null,
              low_rating_loyalty_program_id: form.reviewSettings?.lowRatingLoyaltyProgramId || null,
              updated_at: new Date().toISOString(),
            })
            .eq('id', form.id);

          if (formError) throw formError;

          // Update business star threshold if changed
          if (form.reviewSettings?.starThreshold !== undefined) {
            await supabase
              .from('businesses')
              .update({ review_redirect_star_threshold: form.reviewSettings.starThreshold })
              .eq('id', form.businessId);
          }

          // Delete existing questions for update (not needed for new forms)
          const { error: deleteError } = await supabase
            .from('questions')
            .delete()
            .eq('form_id', form.id);

          if (deleteError) throw deleteError;
        }

        // Insert questions (using actualFormId for new forms)
        if (questions.length > 0) {
          const questionsToInsert = questions.map((q, index) => ({
            id: isNewForm ? crypto.randomUUID() : q.id, // Generate new IDs for new forms
            form_id: actualFormId,
            type: q.type, // Keep legacy type for backwards compatibility
            question_type_v2: q.type,
            text: q.text,
            required: q.required,
            order: index,
            options: q.options?.map((opt) => ({ id: opt.id, value: opt.label })) || null,
            placeholder: q.placeholder || null,
            visible_on_high_rating: q.visibleOnHighRating ?? true,
            visible_on_low_rating: q.visibleOnLowRating ?? true,
            branching_rules: convertBranchingRulesToDB(q.branchingRules),
            validation_rules: q.validationRules || null,
            device_config: q.deviceConfig || null,
            ui_config: q.uiConfig || null,
          }));

          const { error: insertError } = await supabase
            .from('questions')
            .insert(questionsToInsert);

          if (insertError) throw insertError;
        }

        return { success: true, newFormId: isNewForm ? actualFormId : undefined };
      } catch (err) {
        console.error('Error saving form:', err);
        setError(err as Error);
        return { success: false, error: err };
      } finally {
        setSaving(false);
      }
    },
    []
  );

  return { saveForm, saving, error };
}

// Helper: Convert v2 branching rules back to DB format
function convertBranchingRulesToDB(rules: FormQuestion['branchingRules']) {
  if (!rules || rules.length === 0) return null;

  const answers: Record<string, { action: string; targetQuestionId?: string }> = {};

  rules.forEach((rule) => {
    const value = String(rule.conditionValue);
    if (rule.targetQuestionId === 'end') {
      answers[value] = { action: 'skip_to_end' };
    } else if (rule.targetQuestionId === 'google_redirect') {
      answers[value] = { action: 'skip_to_end' }; // Map to skip_to_end for now
    } else {
      answers[value] = {
        action: 'goto_question',
        targetQuestionId: rule.targetQuestionId,
      };
    }
  });

  return { answers };
}

// Convert FormQuestion to NavigableQuestion for the player
export function toNavigableQuestions(questions: FormQuestion[]): NavigableQuestion[] {
  return questions.map((q) => ({
    ...q,
    id: q.id,
  }));
}
